import { cn } from 'src/helpers/style'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

export interface IFundSummaryItem {
  label: string
  value: string
  description: string
}

export interface IFundSummaryStats {
  summaryAsOf: string
  fundSummaryItems: IFundSummaryItem[]
}

export interface IFundSummaryEmptyState {
  title: string
  description: string
}

export interface IFundSummaryProps {
  items: IFundSummaryItem[]
  emptyState?: IFundSummaryEmptyState
}

export const FundSummary = ({
  items,
  emptyState,
}: IFundSummaryProps): JSX.Element => {
  if (items.length === 0 && emptyState) {
    return (
      <div className="flex min-h-[89px] items-center justify-center rounded-xl bg-state-hover-surface">
        <div className="flex flex-col items-center gap-1 text-center">
          <Typography variant={TypographyVariant.title2Xs}>
            {emptyState.title}
          </Typography>
          <Typography
            variant={TypographyVariant.bodyXs}
            className="text-content-secondary"
          >
            {emptyState.description}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-[20px] border border-border-default md:rounded-none md:border-0">
      <div className="grid w-full grid-cols-2 md:grid-cols-4">
        {items.map((item, index): JSX.Element => {
          const isRightColumnOnMobile = index % 2 === 1
          const isBottomRowOnMobile = index >= 2
          const isFirstOnDesktop = index === 0

          return (
            <div
              key={item.label}
              className={cn(
                'flex flex-col gap-1 p-3',
                isRightColumnOnMobile && 'border-l border-border-default',
                isBottomRowOnMobile && 'border-t border-border-default',
                'md:min-h-[104px] md:gap-2 md:py-1 md:pl-5 md:pr-5 md:first:pl-0',
                isBottomRowOnMobile && 'md:border-t-0',
                isFirstOnDesktop
                  ? 'md:border-l-0'
                  : 'md:border-l-2 md:border-border-default',
              )}
            >
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {item.label}
              </Typography>
              <Typography
                variant={TypographyVariant.titleLg}
                weight={TypographyWeight.medium}
                className="text-title-md text-content-primary md:text-title-lg"
              >
                {item.value}
              </Typography>
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {item.description}
              </Typography>
            </div>
          )
        })}
      </div>
    </div>
  )
}
